import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { debounce } from "capitalrx-components-2.0";
import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { Drug, useListDrugsQuery } from "@/App/api/drug";
import { ContentPanel, NoResults, PageWrapper, ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

export const FindBestPriceDrugNameSearchPage: FC = () => {
  const { t: translate } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("drugName") || "");
  const [querySearchTerm, setQuerySearchTerm] = useState(searchParams.get("drugName") || "");
  const navigate = useNavigateClient();

  const { data: drugs, isLoading: isLoadingDrugs } = useListDrugsQuery({ search: querySearchTerm });

  const onSearchChange = useCallback(
    debounce((value: string) => {
      if (value.length >= 3) {
        setQuerySearchTerm(value);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (searchTerm.length >= 3) {
      onSearchChange(searchTerm);
    } else {
      onSearchChange("");
    }
  }, [searchTerm]);

  const onDrugSelected = useCallback((drug: Drug) => {
    setSearchParams(`?drugName=${searchTerm}`);
    navigate(`/find-best-price/${drug.id}?drugName=${searchTerm}`);
  }, [searchTerm]);

  return (
    <PageWrapper>
      <ContentPanel title={translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.findBestPrice")}>
        <Stack>
          <Shelf>
            <Textbox
              testId="drug-name-search"
              style={{ flexGrow: 1 }}
              label={translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.searchByDrugName")}
              value={searchTerm}
              placeholder={translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.searchByDrugName")}
              onChange={setSearchTerm}
            />
          </Shelf>
          <LoadingGroup loading={searchTerm.length >= 3 && isLoadingDrugs}>
            <LoadingGroup.Placeholder
              fallback={
                <Shelf>
                  <Shelf.Spacer />
                  <Spinner size="large" />
                  <Shelf.Spacer />
                </Shelf>
              }
            >
              <Grid spacing="small">
                {(drugs?.results || []).length > 0
                  && drugs.results.map((d) => {
                    return (
                      <Grid.Span columns={12} key={d.drug.id}>
                        <Panel>
                          <Shelf alignment="center">
                            <Subheading testId="drug-name">{d.drug.drug_name}</Subheading>
                            <Shelf.Spacer />
                            <ThemedButton
                              testId="view-pricing"
                              onClick={() => onDrugSelected(d.drug)}
                              iconPosition="right"
                              appearance="secondary"
                              icon="chevron-right"
                            >
                              {translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.selectThisDrug")}
                            </ThemedButton>
                          </Shelf>
                        </Panel>
                      </Grid.Span>
                    );
                  })}
                {querySearchTerm.length >= 3 && (drugs?.results || []).length === 0 && (
                  <NoResults message={`${translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.noResultsFoundFor")} ${querySearchTerm}`} />
                )}
              </Grid>
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
